import { useMemo } from 'react'
import type { RangeKey } from '@dataTypes/dashboard.types'
import {
  getCollection,
  type CollectionSummary,
  getCollectionOverview,
} from '@services/api/dashboard/finance.api'
import {
  computeChartAxis,
  filterYtdPeriodItems,
  formatCurrency,
  formatDaysToCollectGoal,
  goalDollarsToChartUnits,
  formatGoalCurrencyLabel,
  formatPercent,
  toGoalNumber,
} from '@utils/dashboardFinance.util'
import {
  useDashboardPeriodFetch,
  useDashboardPeriodParams,
  type DashboardPeriodFetcher,
} from './useDashboardPeriod'
import { hasNonZeroDashboardValues } from '@utils/dashboardSectionEmpty.util'
import { useClinicGoal } from './useClinicGoal'

export type UseCollectionOptions = {
  range: RangeKey
  date: string
  enabled?: boolean
  queued?: boolean
}

export type CollectionGrossNetChartRow = {
  label: string
  Gross: number
  Net: number
}

export type CollectionSplitChartRow = {
  label: string
  insurance: number
  patient: number
}

export type CollectionDonutSlice = {
  name: string
  value: number
  color: string
}

export type CollectionGrossNetByPeriodRow = {
  period: string
  gross: string
  net: string
  goal: string
  attainment: string
  rate: string
}

export type CollectionPayerSplitRow = {
  period: string
  insurance: string
  patient: string
  total: string
  insuranceShare: string
  patientShare: string
}

export type UseCollectionResult = {
  loading: boolean
  error: string | null
  /** Response has arrived (`data != null`); independent of nonzero content. */
  hasPayload: boolean
  /** Any collection KPI (gross / net / rate / payer split) is nonzero. */
  hasData: boolean
  /** Gross collection is nonzero. */
  hasGrossData: boolean
  /** Net collection is nonzero. */
  hasNetData: boolean
  /** Collection rate is nonzero. */
  hasRateData: boolean
  /** Insurance / patient collection amounts are nonzero. */
  hasPayerSplitData: boolean
  /** Insurance / patient days-to-collect are nonzero. */
  hasDaysToCollectData: boolean
  /** Gross / net by-period chart buckets contain a nonzero value. */
  hasTrendData: boolean
  summary: CollectionSummary | undefined
  changeSignedPct: string
  changeAmt: string
  grossStr: string
  netStr: string
  adjustmentStr: string
  rateStr: string
  insuranceStr: string
  patientStr: string
  insuranceDaysStr: string
  patientDaysStr: string
  grossNetChart: {
    data: CollectionGrossNetChartRow[]
    yDomain: [number, number]
    yTicks: number[]
  }
  splitChart: {
    data: CollectionSplitChartRow[]
    yDomain: [number, number]
    yTicks: number[]
  }
  donut: {
    primaryK: number
    secondaryK: number
    slices: CollectionDonutSlice[]
  }
  grossNetByPeriodRows: CollectionGrossNetByPeriodRow[]
  payerSplitRows: CollectionPayerSplitRow[]
  goalLabel: string
  goalValue: string
  collGoalLabel: string
  collGoalLine: number
  collectionMetricGoalLabel: string
  patientCollectionGoalLabel: string
  insuranceCollectionGoalLabel: string
  patientDaysGoalLabel: string
  insuranceDaysGoalLabel: string
  collectionRateTargetLabel: string
}

function formatDays(value: number): string {
  if (!Number.isFinite(value)) return '—'
  return `${Math.round(value)} days`
}

function useCollectionFromFetcher({
  range,
  date,
  enabled = true,
  queued = false,
  fetcher,
}: UseCollectionOptions & {
  fetcher: DashboardPeriodFetcher<Awaited<ReturnType<typeof getCollection>>>
}): UseCollectionResult {
  const queryParams = useDashboardPeriodParams(range, date)
  const { data, loading, error } = useDashboardPeriodFetch(
    queryParams,
    fetcher,
    { enabled, queued },
  )
  const { goal: clinicGoal } = useClinicGoal({ range, date, enabled, queued })

  const goalLabel = formatGoalCurrencyLabel(clinicGoal?.collection_header_goal)
  const goalValue = formatCurrency(
    toGoalNumber(clinicGoal?.collection_header_goal),
    { compact: true },
  )
  const collGoalLabel = formatGoalCurrencyLabel(
    clinicGoal?.gross_collection_goal,
  )
  const collGoalLine = goalDollarsToChartUnits(
    clinicGoal?.collection_chart_goal,
  )
  const collectionMetricGoalLabel = formatGoalCurrencyLabel(
    clinicGoal?.gross_collection_goal,
  )
  const patientCollectionGoalLabel = formatGoalCurrencyLabel(
    clinicGoal?.patient_collection_dollar_goal,
  )
  const insuranceCollectionGoalLabel = formatGoalCurrencyLabel(
    clinicGoal?.insurance_collection_dollar_goal,
  )
  const patientDaysGoalLabel = formatDaysToCollectGoal(
    clinicGoal?.patient_days_to_collect_target,
  )
  const insuranceDaysGoalLabel = formatDaysToCollectGoal(
    clinicGoal?.insurance_days_to_collect_target,
  )
  const collectionRateTargetPct = toGoalNumber(
    clinicGoal?.collection_rate_target_pct,
  )
  const collectionRateTargetLabel =
    collectionRateTargetPct > 0
      ? `Target ${formatPercent(collectionRateTargetPct, 0)}`
      : 'Target 0%'

  const summary = data?.summary

  const changeSignedPct = useMemo(() => {
    const grossPctChange = summary?.grossCollection.percentChange ?? 0
    return `${grossPctChange >= 0 ? '+' : ''}${Math.abs(grossPctChange).toFixed(0)}%`
  }, [summary])

  const changeAmt = useMemo(() => {
    const gross = summary?.grossCollection.current ?? 0
    const prevGross = summary?.grossCollection.previous ?? 0
    const deltaAmt = gross - prevGross
    return `$${Math.round(deltaAmt).toLocaleString('en-US')}`
  }, [summary])

  const grossStr = useMemo(
    () =>
      formatCurrency(summary?.grossCollection.current ?? 0, { compact: true }),
    [summary],
  )

  const netStr = useMemo(
    () =>
      formatCurrency(summary?.netCollection.current ?? 0, { compact: true }),
    [summary],
  )

  const adjustmentStr = useMemo(
    () => formatPercent(summary?.adjustmentRate.current ?? 0, 1),
    [summary],
  )

  const rateStr = useMemo(
    () => formatPercent(summary?.collectionRate.current ?? 0, 0),
    [summary],
  )

  const insuranceStr = useMemo(
    () =>
      formatCurrency(summary?.insuranceCollection.current ?? 0, {
        compact: true,
      }),
    [summary],
  )

  const patientStr = useMemo(
    () =>
      formatCurrency(summary?.patientCollection.current ?? 0, {
        compact: true,
      }),
    [summary],
  )

  const insuranceDaysStr = useMemo(
    () => formatDays(summary?.insuranceDaysToCollect.current ?? 0),
    [summary],
  )

  const patientDaysStr = useMemo(
    () => formatDays(summary?.patientDaysToCollect.current ?? 0),
    [summary],
  )

  const grossNetChart = useMemo(() => {
    const byTimePeriod = filterYtdPeriodItems(
      range,
      date,
      data?.byTimePeriod ?? [],
      (row) => row.period,
    )
    const chartData: CollectionGrossNetChartRow[] = byTimePeriod.map((row) => ({
      label: row.period,
      Gross: row.grossCollection / 1000,
      Net: row.netCollection / 1000,
    }))
    const values = chartData.flatMap((p) => [p.Gross, p.Net])
    const axis = computeChartAxis(values)
    return {
      data: chartData,
      yDomain: axis.yDomain,
      yTicks: axis.yTicks,
    }
  }, [data, range, date])

  const splitChart = useMemo(() => {
    const byPayerSplit = filterYtdPeriodItems(
      range,
      date,
      data?.byPayerSplit ?? [],
      (row) => row.period,
    )
    const chartData: CollectionSplitChartRow[] = byPayerSplit.map((row) => ({
      label: row.period,
      insurance: row.insuranceCollection / 1000,
      patient: row.patientCollection / 1000,
    }))
    const values = chartData.flatMap((p) => [p.insurance, p.patient])
    const axis = computeChartAxis(values)
    return {
      data: chartData,
      yDomain: axis.yDomain,
      yTicks: axis.yTicks,
    }
  }, [data, range, date])

  const donut = useMemo(() => {
    const insurance = summary?.insuranceCollection.current ?? 0
    const patient = summary?.patientCollection.current ?? 0
    return {
      primaryK: insurance / 1000,
      secondaryK: patient / 1000,
      slices: [
        {
          name: 'Insurance',
          value: insurance / 1000,
          color: 'var(--blue-500)',
        },
        { name: 'Patient', value: patient / 1000, color: 'var(--teal-400)' },
      ],
    }
  }, [summary])

  const grossNetByPeriodRows = useMemo((): CollectionGrossNetByPeriodRow[] => {
    const periodGoal = toGoalNumber(clinicGoal?.gross_collection_goal)
    const byTimePeriod = filterYtdPeriodItems(
      range,
      date,
      data?.byTimePeriod ?? [],
      (row) => row.period,
    )
    return byTimePeriod.map((row) => {
      const attainmentPct =
        periodGoal > 0 ? (row.grossCollection / periodGoal) * 100 : null
      return {
        period: row.period,
        gross: formatCurrency(row.grossCollection, { compact: true }),
        net: formatCurrency(row.netCollection, { compact: true }),
        goal: formatCurrency(periodGoal, { compact: true }),
        attainment:
          attainmentPct != null ? formatPercent(attainmentPct, 0) : '—',
        rate:
          row.grossCollection > 0
            ? formatPercent((row.netCollection / row.grossCollection) * 100, 0)
            : '—',
      }
    })
  }, [data, range, date, clinicGoal])

  const payerSplitRows = useMemo((): CollectionPayerSplitRow[] => {
    const byPayerSplit = filterYtdPeriodItems(
      range,
      date,
      data?.byPayerSplit ?? [],
      (row) => row.period,
    )
    return byPayerSplit.map((row) => ({
      period: row.period,
      insurance: formatCurrency(row.insuranceCollection, { compact: true }),
      patient: formatCurrency(row.patientCollection, { compact: true }),
      total: formatCurrency(row.total, { compact: true }),
      insuranceShare: formatPercent(row.insuranceSharePercent),
      patientShare: formatPercent(row.patientSharePercent),
    }))
  }, [data, range, date])

  const hasGrossData = hasNonZeroDashboardValues(
    summary?.grossCollection.current,
  )
  const hasNetData = hasNonZeroDashboardValues(summary?.netCollection.current)
  const hasRateData = hasNonZeroDashboardValues(summary?.collectionRate.current)
  const hasPayerSplitData = hasNonZeroDashboardValues(
    summary?.insuranceCollection.current,
    summary?.patientCollection.current,
  )
  const hasDaysToCollectData = hasNonZeroDashboardValues(
    summary?.insuranceDaysToCollect.current,
    summary?.patientDaysToCollect.current,
  )
  const hasTrendData = grossNetChart.data.some((row) =>
    hasNonZeroDashboardValues(row.Gross, row.Net),
  )

  return {
    loading,
    error,
    hasPayload: data != null,
    hasData: hasGrossData || hasNetData || hasRateData || hasPayerSplitData,
    hasGrossData,
    hasNetData,
    hasRateData,
    hasPayerSplitData,
    hasDaysToCollectData,
    hasTrendData,
    summary,
    changeSignedPct,
    changeAmt,
    grossStr,
    netStr,
    adjustmentStr,
    rateStr,
    insuranceStr,
    patientStr,
    insuranceDaysStr,
    patientDaysStr,
    grossNetChart,
    splitChart,
    donut,
    grossNetByPeriodRows,
    payerSplitRows,
    goalLabel,
    goalValue,
    collGoalLabel,
    collGoalLine,
    collectionMetricGoalLabel,
    patientCollectionGoalLabel,
    insuranceCollectionGoalLabel,
    patientDaysGoalLabel,
    insuranceDaysGoalLabel,
    collectionRateTargetLabel,
  }
}

export function useCollection(
  options: UseCollectionOptions,
): UseCollectionResult {
  return useCollectionFromFetcher({ ...options, fetcher: getCollection })
}

export function useCollectionOverview(
  options: UseCollectionOptions,
): UseCollectionResult {
  return useCollectionFromFetcher({
    ...options,
    fetcher: getCollectionOverview,
  })
}
